import EmailTemplateButtonGroup from '@/components/Drawer/EmailTemplateButtonGroup'
import {HtmlPreview} from '@/shared-components/HtmlPreview'
import {useGetEmailTemplateDetails} from '@/components/Drawer/hooks/useEmailTemplateDetails'
import {InputSelector} from '@/shared-components/InputSelector/InputSelector'
import {InputText} from '@/shared-components/InputText'
import {FileUploadV2, Icon} from 'pepsico-ds'
import PostalMime from 'postal-mime'
import {MessageTemplateDrawerStatusEnum} from '@/types/MessageTemplateDrawerStatusEnum'
import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {useLocation, useParams} from 'react-router-dom'

interface Props {
  templateName: string
  setTemplateName: (value: string) => void
  category: string
  templateMessage: string
  setTemplateMessage: (value: string) => void
  country: string
  updateDrawerStatus: (value: MessageTemplateDrawerStatusEnum) => void
  closeDrawer: () => void
}

const escapeHtml = (value: string): string =>
  value.replace(
    /[&<>"']/g,
    character =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      })[character] ?? character
  )

export default function CreateEmailTemplatePage({
  closeDrawer,
  country,
  templateName,
  setTemplateName,
  category,
  templateMessage,
  setTemplateMessage,
  updateDrawerStatus,
}: Props): React.JSX.Element {
  const {formatMessage} = useIntl()
  const [fileName, setFileName] = useState<string>('')
  const [error, setError] = useState<string>('')
  const {pathname} = useLocation()
  const {id: emailTemplateId} = useParams()
  const isEditingTemplate = pathname?.includes('/edit')
  const {
    data: templateDetailsData,
    isFetching,
    error: templateDetailsError,
  } = useGetEmailTemplateDetails(emailTemplateId)

  useEffect(() => {
    if (isEditingTemplate && !isFetching && !templateDetailsError && templateDetailsData) {
      setTemplateName(templateDetailsData.name)
      setTemplateMessage(templateDetailsData.message ?? '')
    }
  }, [isEditingTemplate, isFetching, templateDetailsError, templateDetailsData, setTemplateName, setTemplateMessage])

  const handleFileUpload = async (file: File) => {
    setError('')

    try {
      const fileExtension = file.name.toLowerCase().split('.').pop()
      let parsedHtml = ''

      if (fileExtension === 'html' || fileExtension === 'htm' || file.type === 'text/html') {
        parsedHtml = await file.text()
      } else if (fileExtension === 'eml') {
        const email = await new PostalMime().parse(await file.arrayBuffer())
        parsedHtml = email.html ?? (email.text ? `<pre>${escapeHtml(email.text)}</pre>` : '')
      }

      if (!parsedHtml) {
        setFileName('')
        setTemplateMessage('')
        setError(formatMessage({id: 'EMAIL_TEMPLATE_BUILDER.INVALID_EMAIL_FILE'}))
        return
      }

      setFileName(file.name)
      setTemplateMessage(parsedHtml)
    } catch {
      setFileName('')
      setTemplateMessage('')
      setError(formatMessage({id: 'EMAIL_TEMPLATE_BUILDER.INVALID_EMAIL_FILE'}))
    }
  }

  const handleFileError = (message: string) => {
    setError(message)
  }

  return (
    <div className="ml-2 mt-1 overflow-auto px-1" data-testid="create-email-template-container">
      <div className="mb-4 text-lg font-bold text-text-primary">
        {formatMessage({id: 'EMAIL_TEMPLATE_BUILDER.CREATE_EMAIL_TEMPLATE_HEADER'})}
      </div>
      <div className="mb-4 text-sm text-text-secondary">
        {formatMessage({id: 'EMAIL_TEMPLATE_BUILDER.CREATE_EMAIL_TEMPLATE_SUBHEADER'})}
      </div>
      <div className="h-42 mb-4 flex w-full max-w-xs flex-col gap-1">
        <InputSelector
          id="email-template-country"
          value={country}
          label={formatMessage({id: 'EMAIL_TEMPLATE_BUILDER.COUNTRY_LABEL'})}
          required
          disabled
        >
          <option value={country}>{country}</option>
        </InputSelector>
      </div>
      <div className="h-42 mb-4 flex w-full max-w-xs flex-col gap-1">
        <InputText
          id="email-template-name"
          label={formatMessage({id: 'EMAIL_TEMPLATE_BUILDER.EMAIL_TEMPLATE_NAME'})}
          placeholder={formatMessage({id: 'EMAIL_TEMPLATE_BUILDER.EMAIL_TEMPLATE_NAME_PLACEHOLDER'})}
          value={templateName}
          onChange={event => setTemplateName(event.target.value)}
          maxLength={100}
          required
        />
      </div>
      <div className="h-42 mb-4 flex w-full max-w-xs flex-col gap-1">
        <InputSelector
          id="email-template-category"
          value={category}
          label={formatMessage({id: 'EMAIL_TEMPLATE_BUILDER.EMAIL_TEMPLATE_CATEGORY'})}
          required
          disabled
        >
          <option value="RTN">RTN</option>
        </InputSelector>
      </div>
      <div className="w-full max-w-xs">
        <FileUploadV2
          className="send-email-upload"
          data-testid="email-template-file-upload"
          buttonLabel={formatMessage({id: 'EMAIL_TEMPLATE_BUILDER.UPLOAD_FILE'})}
          description={formatMessage({id: 'EMAIL_TEMPLATE_BUILDER.UPLOAD_FILE_DESCRIPTION'})}
          onUpdate={handleFileUpload}
          onFileError={handleFileError}
          accept=".eml,.html,.htm"
          onValidateFile={file => {
            const fileExtension = file.name.toLowerCase().split('.').pop()
            const isSupportedFile = fileExtension === 'eml' || fileExtension === 'html' || fileExtension === 'htm'
            return isSupportedFile && file.size <= 5 * 1024 * 1024
          }}
        />
      </div>
      {error && <p className="text-text-error mt-2 text-sm">{error}</p>}
      {fileName && (
        <div className="mt-2 flex items-center gap-2 text-xs text-text-secondary">
          <Icon icon="check" size="small" />
          <span>{fileName}</span>
        </div>
      )}
      {templateMessage && (
        <div className="mt-6">
          <div className="mb-2 text-sm font-bold text-text-secondary">
            {formatMessage({id: 'EMAIL_TEMPLATE_BUILDER.EMAIL_TEMPLATE_PREVIEW'})}
          </div>
          <HtmlPreview html={templateMessage} data-testid="create-email-template-preview" />
        </div>
      )}
      <EmailTemplateButtonGroup
        testIdPrimary="create-email-template-confirm-button"
        testIdTertiary="create-email-template-cancel-button"
        primaryButtonLabel={formatMessage({id: 'EMAIL_TEMPLATE_BUILDER.SAVE_BUTTON_LABEL'})}
        tertiaryButtonLabel={formatMessage({id: 'EMAIL_TEMPLATE_BUILDER.CANCEL_BUTTON_LABEL'})}
        onClickPrimary={() => {
          updateDrawerStatus(MessageTemplateDrawerStatusEnum.emailTemplatePreview)
        }}
        onClickTertiary={closeDrawer}
        isPrimaryDisabled={!templateMessage || !templateName.trim()}
      />
    </div>
  )
}
